/**
 * Square-grid helpers for board games (checkers now; chess and battleship next).
 * Pure and platform-free. A square is an integer index `y * size + x`; x is the file
 * (0 = "a") and y the rank (0 = rank 1, the first player's home edge).
 */

export type Step = readonly [dx: number, dy: number];

export const DIAGONALS: readonly Step[] = [[1, 1], [-1, 1], [1, -1], [-1, -1]];
export const ORTHOGONALS: readonly Step[] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
/** The eight king / queen directions: orthogonals then diagonals. */
export const ALL_DIRECTIONS: readonly Step[] = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]];
export const KNIGHT_STEPS: readonly Step[] = [[1, 2], [2, 1], [-1, 2], [-2, 1], [1, -2], [2, -1], [-1, -2], [-2, -1]];

export const inBounds = (size: number, x: number, y: number): boolean => Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < size && y < size;

export const toIndex = (size: number, x: number, y: number): number => y * size + x;
export const xOf = (size: number, i: number): number => i % size;
export const yOf = (size: number, i: number): number => Math.floor(i / size);

/** Algebraic label of a square: "a1" is x=0,y=0. Supports boards up to 26 wide. */
export const label = (size: number, i: number): string => `${String.fromCharCode(97 + xOf(size, i))}${yOf(size, i) + 1}`;
export const fileLabel = (x: number): string => String.fromCharCode(97 + x);
export const rankLabel = (y: number): string => String(y + 1);

/** Square index for a label such as "c3", or null when malformed or off the board. */
export function parseLabel(size: number, text: string): number | null {
  const m = /^([a-z])([1-9]\d?)$/.exec(text);
  if (!m) return null;
  const x = (m[1] as string).charCodeAt(0) - 97;
  const y = Number(m[2]) - 1;
  return inBounds(size, x, y) ? toIndex(size, x, y) : null;
}

/** The square `k` steps from `i` in direction `d`, or null when that leaves the board. */
export function stepFrom(size: number, i: number, d: Step, k = 1): number | null {
  const x = xOf(size, i) + d[0] * k;
  const y = yOf(size, i) + d[1] * k;
  return inBounds(size, x, y) ? toIndex(size, x, y) : null;
}

/** Squares from `i` (exclusive) to the edge along `d`, nearest first. */
export function ray(size: number, i: number, d: Step): number[] {
  const out: number[] = [];
  for (let k = 1; ; k++) {
    const s = stepFrom(size, i, d, k);
    if (s === null) return out;
    out.push(s);
  }
}

/** Chessboard colouring with a1 dark. */
export const isDarkSquare = (size: number, i: number): boolean => (xOf(size, i) + yOf(size, i)) % 2 === 0;

/** The square midway between two squares exactly two steps apart in a line (a jumped square), else null. */
export function between(size: number, a: number, b: number): number | null {
  const dx = xOf(size, b) - xOf(size, a);
  const dy = yOf(size, b) - yOf(size, a);
  if (Math.abs(dx) % 2 !== 0 || Math.abs(dy) % 2 !== 0 || (dx === 0 && dy === 0)) return null;
  return toIndex(size, xOf(size, a) + dx / 2, yOf(size, a) + dy / 2);
}

export interface DisplayCell {
  readonly index: number;
  readonly x: number;
  readonly y: number;
  /** 0 = top row on screen. */
  readonly row: number;
  readonly col: number;
}

/**
 * Cells in screen order (top row first, left to right). Unflipped puts rank 1 at the bottom
 * (the first player's view); flipped turns the board half a turn for the second player.
 */
export function displayCells(size: number, flip = false): DisplayCell[] {
  const out: DisplayCell[] = [];
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      const x = flip ? size - 1 - col : col;
      const y = flip ? row : size - 1 - row;
      out.push({ index: toIndex(size, x, y), x, y, row, col });
    }
  }
  return out;
}

/** Row letter of a square row: 0 = "A". Supports boards up to 26 tall. */
export const rowLetter = (y: number): string => String.fromCharCode(65 + y);
export const colNumber = (x: number): string => String(x + 1);

/** Row-then-column label ("A1" is the top-left square, "J10" the bottom-right of a 10x10): the battleship call. */
export const rowColLabel = (size: number, i: number): string => `${rowLetter(yOf(size, i))}${colNumber(xOf(size, i))}`;

/** Square index for a row-column label such as "C7" (case-insensitive), or null when malformed or off the board. */
export function parseRowCol(size: number, text: string): number | null {
  const m = /^([A-Za-z])([1-9]\d?)$/.exec(text);
  if (!m) return null;
  const y = (m[1] as string).toUpperCase().charCodeAt(0) - 65;
  const x = Number(m[2]) - 1;
  return inBounds(size, x, y) ? toIndex(size, x, y) : null;
}

/** The `length` squares starting at `i` and running along `d`, or null if any would leave the board. */
export function lineCells(size: number, i: number, d: Step, length: number): number[] | null {
  const out: number[] = [];
  for (let k = 0; k < length; k++) {
    const s = stepFrom(size, i, d, k);
    if (s === null) return null;
    out.push(s);
  }
  return out;
}

/** The up to eight squares touching `i` (sides and corners). */
export function neighbors(size: number, i: number): number[] {
  const out: number[] = [];
  for (const d of ALL_DIRECTIONS) {
    const s = stepFrom(size, i, d);
    if (s !== null) out.push(s);
  }
  return out;
}

/** Cells in screen order with row 0 (y = 0) at the top and x running left to right: a battleship chart. */
export function displayCellsTopDown(size: number): DisplayCell[] {
  const out: DisplayCell[] = [];
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) out.push({ index: toIndex(size, x, y), x, y, row: y, col: x });
  return out;
}
